import type {
  TitleJournalOrgCheckInput,
  TitleJournalOrgCheckParticipant,
} from "./title-journal-org-check.types";

type TitleOrgRole =
  | "buildingContractor"
  | "developer"
  | "technicalCustomer"
  | "projectDocumentationContractor"
  | "designerSupervision"
  | "otherDeveloper";

const TITLE_ORG_ROLE_PRIORITY: Record<TitleOrgRole, number> = {
  buildingContractor: 0,
  developer: 1,
  technicalCustomer: 2,
  projectDocumentationContractor: 3,
  designerSupervision: 4,
  otherDeveloper: 5,
};

function shouldReplaceTitleOrgRole(
  current: TitleOrgRole,
  candidate: TitleOrgRole,
): boolean {
  return (
    TITLE_ORG_ROLE_PRIORITY[candidate] < TITLE_ORG_ROLE_PRIORITY[current]
  );
}

function toCompanyOption(
  participant: TitleJournalOrgCheckParticipant,
  role: TitleOrgRole,
): { companyId: string; projectParticipantId: string; role: TitleOrgRole } | null {
  const companyId = participant.company?.companyId;
  const { projectParticipantId } = participant;

  if (!companyId || !projectParticipantId) return null;

  return { companyId, projectParticipantId, role };
}

function getTitleJournalCompanies(
  titleJournal: TitleJournalOrgCheckInput,
): Array<{ companyId: string; projectParticipantId: string; role: TitleOrgRole }> {
  const title = titleJournal?.mainJournal?.titleJournal;
  if (!title) return [];

  const entries: Array<{
    companyId: string;
    projectParticipantId: string;
    role: TitleOrgRole;
  }> = [];

  const push = (
    participant: TitleJournalOrgCheckParticipant | null | undefined,
    role: TitleOrgRole,
  ) => {
    if (!participant) return;
    const option = toCompanyOption(participant, role);
    if (option) entries.push(option);
  };

  push(title.buildingContractor, "buildingContractor");
  push(title.developer, "developer");
  push(title.technicalCustomer, "technicalCustomer");
  push(title.projectDocumentationContractor, "projectDocumentationContractor");

  for (const participant of title.designerSupervisions ?? []) {
    push(participant, "designerSupervision");
  }

  for (const participant of title.otherDevelopers ?? []) {
    push(participant, "otherDeveloper");
  }

  const unique = new Map<
    string,
    { companyId: string; projectParticipantId: string; role: TitleOrgRole }
  >();
  for (const entry of entries) {
    const existing = unique.get(entry.projectParticipantId);
    if (!existing || shouldReplaceTitleOrgRole(existing.role, entry.role)) {
      unique.set(entry.projectParticipantId, entry);
    }
  }

  return Array.from(unique.values());
}

export function resolveUserTitleCompanyMatch(
  titleJournal: TitleJournalOrgCheckInput,
  companyIds: string[],
): boolean {
  if (!companyIds.length) return false;

  const title = titleJournal?.mainJournal?.titleJournal;
  const companyIdSet = new Set(companyIds);

  const losCompanyId = title?.buildingContractor?.company?.companyId;
  const losProjectParticipantId = title?.buildingContractor?.projectParticipantId;

  if (
    losCompanyId &&
    losProjectParticipantId &&
    companyIdSet.has(losCompanyId)
  ) {
    return true;
  }

  const titleCompanies = getTitleJournalCompanies(titleJournal);

  return titleCompanies.some((company) => companyIdSet.has(company.companyId));
}
